// Feb 18, 2026 — Unified Subscribe popup for Public Q&A pages.
//
// Two variants based on silo.subscriber_price_cents:
//   - $0    → free-subscribe (bypass Stripe). Writes a real subscription
//             row server-side via POST /silos/{slug}/free-follow.
//   - $X    → opens Stripe checkout via POST /stripe/fan/checkout.
//
// Auth-gate happens BEFORE this component renders — the calling page
// must redirect anon visitors to /login (preserving ref-seed) and only
// open this popup for logged-in users.
//
// After successful subscription, fires the A2HS popup (high-intent
// moment to install to home screen) and closes itself.
import { useState } from "react";
import { X, Sparkle, BookmarkSimple, ChatCircle, Heart, Bell, CheckCircle } from "@phosphor-icons/react";
import AutoRenewDisclosure from "@/components/AutoRenewDisclosure";
import { toast } from "sonner";
import { api } from "@/lib/api";
import A2HSPopup from "@/components/A2HSPopup";
import { supporterFeeCents, feeConfigFromSilo, money } from "@/lib/supporterFee";

// Perk list — derived from the actual gating in `silo_to_public` and the
// `viewer_tier` branch in /ask, /bookmarks, /chat. Reflects what really
// changes when a fan becomes a subscriber.
const PERKS = [
  { icon: ChatCircle, label: "Generous question allowance", body: "Ask freely — your subscription covers a generous monthly allowance. A friendly nudge invites a tip only if you ever reach it." },
  { icon: BookmarkSimple, label: "Save your favorites", body: "Bookmark answers you love. Fans can only keep the top one — subscribers keep them all." },
  { icon: Heart, label: "Subscribed badge", body: "You show up as Subscribed in your Fan Portal and on this creator's people list." },
  { icon: Bell, label: "First to know", body: "If pricing or content changes, you'll see it before anyone else." },
];

export default function SubscribePopup({ open, onClose, silo, slug, onSubscribed }) {
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!open || !silo) return null;

  const priceCents = silo.subscriber_price_cents || 0;
  const isFree = priceCents <= 0;
  const creatorName = silo.name || "this silo";
  // Sep 25, 2026 — Supporter Service Fee (Model A). The creator receives the
  // price they set; the supporter pays that plus a disclosed fee. Priced
  // server-side and delivered on /s/{slug}; the shared calculator is the
  // fallback so a stale payload can never under-quote the total.
  const feeCents = silo.subscriber_fee_cents != null
    ? silo.subscriber_fee_cents
    : supporterFeeCents(priceCents, feeConfigFromSilo(silo));
  const totalCents = silo.subscriber_total_cents ?? (priceCents + feeCents);

  const handleSubscribe = async () => {
    setBusy(true);
    try {
      if (isFree) {
        await api.post(`/silos/${slug}/free-follow`);
        toast.success(`You're subscribed to ${creatorName} 💛`);
        setSuccess(true);
        if (onSubscribed) onSubscribed();
      } else {
        const r = await api.post("/stripe/fan/checkout", {
          slug,
          origin_url: window.location.origin,
        });
        // Feb 20, 2026 — backend returns `url`, not `checkout_url`. Accept
        // both keys so older deploys + new builds both work.
        const url = r.data?.url || r.data?.checkout_url;
        if (url) {
          window.location.href = url;
          return;
        }
        toast.error("Could not open Stripe checkout. Try again?");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not complete subscription.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={() => !busy && onClose()}
        data-testid="subscribe-popup-backdrop"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="modal-safe bg-[#FDFCF8] w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl border hairline overflow-hidden"
          data-testid="subscribe-popup"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b hairline">
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="duotone" className="text-[#C16E5A]" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#6B6A68]">
                {isFree ? "Free subscription" : "Subscribe"}
              </span>
            </div>
            <button
              onClick={() => !busy && onClose()}
              disabled={busy}
              data-testid="subscribe-popup-close"
              className="text-[#6B6A68] hover:text-[#1C1B1A] disabled:opacity-40"
            >
              <X size={18} />
            </button>
          </div>

          <div className="px-5 sm:px-7 py-6">
            {!success ? (
              <>
                <h2 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">
                  {isFree
                    ? `Congrats — ${creatorName} is free right now.`
                    : `Welcome to ${creatorName}.`}
                </h2>
                <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
                  {isFree
                    ? "This creator gifts their silo for free — you get a generous monthly allowance on them. If you ever use it up, a friendly nudge invites a tip. Pricing changes? You'll hear first."
                    : "Here's exactly what you'll pay. Cancel anytime from your Fan Portal."}
                </p>

                {!isFree && (
                  <div className="mt-4 rounded-lg border hairline bg-white px-4 py-3 text-sm"
                    data-testid="subscribe-price-block">
                    <div className="flex items-center justify-between py-1">
                      <span className="text-[#6B6A68]">Creator subscription</span>
                      <span className="text-[#1C1B1A] font-mono tabular-nums"
                        data-testid="subscribe-price-creator">{money(priceCents)}</span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <span className="text-[#6B6A68] flex items-center gap-1.5">
                        Supporter service fee
                        <span title="Covers My Silo's platform costs and payment processing."
                          data-testid="subscribe-fee-info"
                          className="w-4 h-4 rounded-full border hairline text-[10px] leading-none flex items-center justify-center text-[#6B6A68] cursor-help">
                          i
                        </span>
                      </span>
                      <span className="text-[#1C1B1A] font-mono tabular-nums"
                        data-testid="subscribe-price-fee">{money(feeCents)}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 mt-1 border-t hairline">
                      <span className="text-[#1C1B1A] font-medium">Total</span>
                      <span className="text-[#1C1B1A] font-medium font-mono tabular-nums"
                        data-testid="subscribe-price-total">{money(totalCents)}/month</span>
                    </div>
                    <p className="text-[11px] text-[#6B6A68] mt-2 leading-relaxed">
                      {money(priceCents)} goes to {creatorName}. The service fee covers My Silo's
                      platform costs and payment processing.
                    </p>
                  </div>
                )}

                <div className="mt-5 space-y-3" data-testid="subscribe-popup-perks">
                  {PERKS.map((p, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
                        <p.icon size={14} weight="duotone" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-[#1C1B1A]">{p.label}</div>
                        <div className="text-xs text-[#6B6A68] leading-relaxed">{p.body}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={handleSubscribe}
                  disabled={busy}
                  data-testid="subscribe-popup-cta"
                  className="mt-7 w-full px-4 py-3 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60 text-sm flex items-center justify-center gap-2"
                >
                  {busy
                    ? "One moment…"
                    : isFree
                      ? `Subscribe to ${creatorName} · Free`
                      : `Continue to Stripe · ${money(totalCents)}/mo`}
                </button>
                {!isFree && (
                  <>
                    <AutoRenewDisclosure
                      priceLabel={`${money(totalCents)}/month`}
                      dense
                      className="mt-3"
                    />
                    <p className="text-[10px] text-[#A09E9B] text-center mt-2">
                      Stripe handles the payment. We never see your card details.
                    </p>
                  </>
                )}
              </>
            ) : (
              <div className="text-center py-4" data-testid="subscribe-popup-success">
                <div className="w-14 h-14 rounded-full bg-[#E8F0E8] text-[#4A5D4E] flex items-center justify-center mx-auto">
                  <CheckCircle size={28} weight="duotone" />
                </div>
                <h2 className="font-serif-display text-2xl text-[#1C1B1A] mt-4">
                  You're in.
                </h2>
                <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
                  Welcome to {creatorName}. Your subscription is active — chat freely, bookmark everything, and you'll find this silo in your Fan Portal.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="subscribe-popup-done"
                  className="mt-5 px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm"
                >
                  Start chatting
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* High-intent A2HS prompt fires right after successful free-subscribe.
          Paid subscribers see this on return from Stripe via the existing
          ?stripe=fan_success handler on PublicSilo. */}
      {success && <A2HSPopup context="post_subscribe" triggerNow={true} />}
    </>
  );
}
